import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, Check, UserPlus, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Avatar } from '../ui/Avatar';

interface SearchResult {
  user_id: string;
  display_name: string;
  username: string;
  mutual_friends: number;
}

type Relation = 'self' | 'friend' | 'pending_outgoing' | 'pending_incoming' | 'none';

interface Props {
  myUserId: string;
  friendIds: string[];
  outgoingTargetIds: string[];
  incomingFromIds: string[];
  onSendRequest: (toUserId: string) => Promise<{ ok: boolean; reason?: string }>;
  onSent: (displayName: string) => void;
}

const SEARCH_RE = /^[a-z0-9_]{3,20}$/;

export function UsernameSearch({
  myUserId,
  friendIds,
  outgoingTargetIds,
  incomingFromIds,
  onSendRequest,
  onSent,
}: Props) {
  const [raw, setRaw] = useState('');
  const [debounced, setDebounced] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [optimisticSent, setOptimisticSent] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sanitized = raw.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (sanitized.length < 3) {
      setDebounced('');
      setResult(null);
      setNotFound(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      setDebounced(sanitized);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [sanitized]);

  useEffect(() => {
    if (!debounced || !SEARCH_RE.test(debounced)) {
      setSearching(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('profiles_public')
        .select('user_id, display_name, username')
        .eq('username', debounced)
        .maybeSingle();

      if (cancelled) return;

      if (!data) {
        setResult(null);
        setNotFound(true);
        setSearching(false);
        return;
      }

      // Calcular amigos em comum: amigos meus que também são amigos do alvo.
      let mutualCount = 0;
      if (data.user_id !== myUserId && friendIds.length > 0) {
        const { count } = await supabase
          .from('friendships')
          .select('friend_id', { count: 'exact', head: true })
          .eq('user_id', data.user_id)
          .in('friend_id', friendIds);
        mutualCount = count ?? 0;
      }

      if (cancelled) return;
      setResult({
        user_id: data.user_id,
        display_name: data.display_name,
        username: data.username,
        mutual_friends: mutualCount,
      });
      setNotFound(false);
      setSearching(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, myUserId, friendIds]);

  const computeRelation = (target: SearchResult): Relation => {
    if (target.user_id === myUserId) return 'self';
    if (friendIds.includes(target.user_id)) return 'friend';
    if (outgoingTargetIds.includes(target.user_id)) return 'pending_outgoing';
    if (incomingFromIds.includes(target.user_id)) return 'pending_incoming';
    if (optimisticSent.includes(target.user_id)) return 'pending_outgoing';
    return 'none';
  };

  const handleAdd = async () => {
    if (!result) return;
    setSending(true);
    const res = await onSendRequest(result.user_id);
    setSending(false);
    if (res.ok) {
      setOptimisticSent((prev) => [...prev, result.user_id]);
      onSent(result.display_name);
    }
  };

  const relation = result ? computeRelation(result) : 'none';

  return (
    <section className="mb-5 px-1">
      <div
        className={[
          'h-11 px-3 flex items-center gap-2 rounded-md border-[1.5px] bg-white',
          'border-ink-300 focus-within:border-green-700',
        ].join(' ')}
      >
        <Search size={16} strokeWidth={2.25} className="text-ink-500 shrink-0" />
        {sanitized.length > 0 && (
          <span className="font-display font-bold text-ink-500 select-none">@</span>
        )}
        <input
          type="text"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Buscar por @username"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          className="flex-1 bg-transparent text-body font-body text-ink-900 outline-none"
          aria-label="Buscar amigo por username"
        />
        {searching && (
          <Loader2 size={14} className="animate-spin text-green-700 shrink-0" />
        )}
      </div>

      {result && (
        <article className="mt-3 bg-white border border-ink-300 rounded-xl p-3 flex items-center gap-3">
          <Avatar name={result.display_name} size="md" />
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-[14px] text-ink-900 truncate">
              {result.display_name}
            </p>
            <p className="text-caption text-ink-500 truncate">@{result.username}</p>
            {result.mutual_friends > 0 && relation !== 'self' && (
              <p className="text-caption font-display font-semibold text-green-700 mt-0.5">
                {result.mutual_friends} {result.mutual_friends === 1 ? 'amigo' : 'amigos'} em comum
              </p>
            )}
          </div>

          {relation === 'self' && (
            <button
              type="button"
              disabled
              className="px-3 h-9 rounded-md bg-ink-100 text-ink-500 font-display font-bold text-[12px] cursor-not-allowed"
            >
              Você
            </button>
          )}
          {relation === 'friend' && (
            <button
              type="button"
              disabled
              className="px-3 h-9 rounded-md border-[1.5px] border-green-700 text-green-700 font-display font-bold text-[12px] inline-flex items-center gap-1.5 cursor-not-allowed"
            >
              <Check size={14} strokeWidth={2.5} /> Amigos
            </button>
          )}
          {relation === 'pending_outgoing' && (
            <button
              type="button"
              disabled
              className="px-3 h-9 rounded-md bg-ink-100 text-ink-500 font-display font-bold text-[12px] inline-flex items-center gap-1.5 cursor-not-allowed"
            >
              <Clock size={14} strokeWidth={2.25} /> Pedido enviado
            </button>
          )}
          {relation === 'pending_incoming' && (
            <span className="px-3 h-9 inline-flex items-center rounded-md bg-gold-100 text-ink-900 font-display font-bold text-[12px]">
              Já te enviou pedido
            </span>
          )}
          {relation === 'none' && (
            <button
              type="button"
              onClick={handleAdd}
              disabled={sending}
              className="px-3 h-9 rounded-md bg-green-700 text-white font-display font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-green-900 btn-focus disabled:opacity-60"
            >
              {sending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <UserPlus size={14} strokeWidth={2.25} />
              )}
              Adicionar
            </button>
          )}
        </article>
      )}

      {notFound && sanitized.length >= 3 && !searching && (
        <div className="mt-3 bg-white border border-dashed border-ink-300 rounded-xl py-6 px-4 text-center">
          <p className="font-display font-bold text-[14px] text-ink-900">
            Nenhum usuário com @{sanitized} encontrado
          </p>
          <p className="text-small text-ink-500 mt-1">Verifique se o username está correto.</p>
        </div>
      )}
    </section>
  );
}
